/**
 * Report Filters Component
 *
 * Shared date range picker for reports. Besides free dates and calendar
 * presets it offers the org's pay periods: choosing one snaps both dates to
 * that period (local calendar days, same as the Pay Periods page shows) and
 * applies at once, so payroll can be run for a period without first visiting
 * the Pay Periods view. The select mirrors the dates — a range that matches a
 * period (e.g. arrived at via the period's "View Payroll Report" link) shows
 * that period as selected; editing either date clears it.
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listPayPeriods } from '../../api/payPeriods';
import { useOrganization } from '../../context/OrganizationContext';
import { queryKeys } from '../../lib/queryClient';
import { formatShortDate, toDateInputValue } from '../../utils/dateFormatting';
import type { PayPeriod } from '../../types/models';
import { Select } from '../ui';

export interface DateRange {
  startDate: string;
  endDate: string;
}

interface ReportFiltersProps {
  dateRange: DateRange;
  onChange: (range: DateRange) => void;
  onExportCSV?: () => void;
  isExporting?: boolean;
  exportLabel?: string;
}

export default function ReportFilters({
  dateRange,
  onChange,
  onExportCSV,
  isExporting = false,
  exportLabel = 'Export CSV',
}: ReportFiltersProps) {
  const [startDate, setStartDate] = useState(dateRange.startDate);
  const [endDate, setEndDate] = useState(dateRange.endDate);
  const { currentOrg } = useOrganization();

  // Update local state when props change
  useEffect(() => {
    setStartDate(dateRange.startDate);
    setEndDate(dateRange.endDate);
  }, [dateRange]);

  // Newest first (the API's order); one page of 100 covers years of periods.
  // Cached across the 13 report pages — periods change a few times a year.
  // The picker is a convenience: on error the date inputs still work.
  const { data: payPeriods = [] } = useQuery({
    queryKey: queryKeys.payPeriods.list(currentOrg?.orgSlug ?? ''),
    queryFn: async () => (await listPayPeriods({ limit: 100 })).payPeriods,
    staleTime: 5 * 60 * 1000,
  });

  const handleApply = () => {
    onChange({ startDate, endDate });
  };

  // The select carries no state of its own: whichever period's days equal the
  // dates in the inputs is the selected one, so editing a date clears it.
  const selectedPayPeriodId =
    payPeriods.find(
      (period) =>
        toDateInputValue(period.startDate) === startDate &&
        toDateInputValue(period.endDate) === endDate
    )?.id ?? '';

  const snapToPayPeriod = (payPeriodId: string) => {
    const period = payPeriods.find((p) => p.id === payPeriodId);
    if (!period) return;
    const range = {
      startDate: toDateInputValue(period.startDate),
      endDate: toDateInputValue(period.endDate),
    };
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    onChange(range);
  };

  // Quick select presets
  const setPreset = (preset: string) => {
    const today = new Date();
    let start: Date;
    let end: Date = today;

    switch (preset) {
      case 'thisMonth':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'lastMonth':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'thisQuarter':
        const quarter = Math.floor(today.getMonth() / 3);
        start = new Date(today.getFullYear(), quarter * 3, 1);
        end = new Date(today.getFullYear(), quarter * 3 + 3, 0);
        break;
      case 'thisYear':
        start = new Date(today.getFullYear(), 0, 1);
        end = new Date(today.getFullYear(), 11, 31);
        break;
      case 'last30':
        start = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'last90':
        start = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      default:
        return;
    }

    const newStartDate = formatDateForInput(start);
    const newEndDate = formatDateForInput(end);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    onChange({ startDate: newStartDate, endDate: newEndDate });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-end gap-4">
        {/* Date inputs */}
        <div className="flex flex-col sm:flex-row gap-4 flex-1">
          <div className="flex-1">
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-rack focus:border-rack"
            />
          </div>
          <div className="flex-1">
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-rack focus:border-rack"
            />
          </div>
          <button
            onClick={handleApply}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors self-end"
          >
            Apply
          </button>
        </div>

        {/* Pay period snap */}
        <div className="lg:w-64">
          <Select
            id="reportPayPeriod"
            label="Pay Period"
            fullWidth
            value={selectedPayPeriodId}
            onChange={snapToPayPeriod}
            disabled={payPeriods.length === 0}
            options={[
              { value: '', label: payPeriods.length === 0 ? 'No pay periods yet' : 'Snap to a pay period…' },
              ...payPeriods.map((period) => ({ value: period.id, label: payPeriodOptionLabel(period) })),
            ]}
          />
        </div>

        {/* Quick presets */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setPreset('thisMonth')}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            This Month
          </button>
          <button
            onClick={() => setPreset('lastMonth')}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Last Month
          </button>
          <button
            onClick={() => setPreset('thisQuarter')}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            This Quarter
          </button>
          <button
            onClick={() => setPreset('thisYear')}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            This Year
          </button>
        </div>

        {/* Export button */}
        {onExportCSV && (
          <button
            onClick={onExportCSV}
            disabled={isExporting}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            {isExporting ? 'Exporting...' : exportLabel}
          </button>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

// The presets above build device-calendar Dates (`new Date(y, m, 1)`), so
// read the same device-calendar fields back. toDateInputValue would read them
// in the org's zone, which east of the org is the previous day. The UTC day
// (`toISOString().split('T')[0]`) is tomorrow after 8pm Eastern.
function formatDateForInput(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function payPeriodOptionLabel(period: PayPeriod): string {
  const status = period.status === 'CLOSED' ? ' · closed' : '';
  return `${period.name} (${formatShortDate(period.startDate)} – ${formatShortDate(period.endDate)})${status}`;
}

// Get default date range (last 30 days)
export function getDefaultDateRange(): DateRange {
  const today = new Date();
  const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  return {
    startDate: formatDateForInput(thirtyDaysAgo),
    endDate: formatDateForInput(today),
  };
}
